import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { track } from "@/lib/analytics";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        const token = localStorage.getItem("discover_token");
        if (!token) { setLoading(false); return; }
        try {
            const r = await api.get("/auth/me");
            setUser(r.data);
        } catch { setUser(null); }
        setLoading(false);
    }, []);

    useEffect(() => {
        // CRITICAL: If returning from Google OAuth, skip the /auth/me check.
        // GoogleAuthCallback will exchange the session_id and call setUser
        // BEFORE we want to trust the existing token.
        if (typeof window !== "undefined"
            && window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        refresh();
    }, [refresh]);

    const login = useCallback(async (email, password) => {
        const r = await api.post("/auth/login", { email, password });
        localStorage.setItem("discover_token", r.data.token);
        setUser(r.data.user);
        try { track("login_success", { method: "password" }); } catch { /* noop */ }
        return r.data.user;
    }, []);
    const register = useCallback(async (data) => {
        const r = await api.post("/auth/register", data);
        localStorage.setItem("discover_token", r.data.token);
        setUser(r.data.user);
        try { track("register_success", { referred: !!data?.referral_code }); } catch { /* noop */ }
        return r.data.user;
    }, []);
    const logout = useCallback(() => {
        try { track("logout"); } catch { /* noop */ }
        localStorage.removeItem("discover_token");
        setUser(null);
    }, []);

    const value = useMemo(
        () => ({ user, loading, login, register, logout, refresh, setUser }),
        [user, loading, login, register, logout, refresh],
    );

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
